import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { notifications } from '@mantine/notifications';

export interface NotificationRecipient {
  type: 'user' | 'email';
  user_id?: string;
  email?: string;
  name?: string;
}

export interface NotificationSettings {
  id: string;
  organization_id: string;

  // Audit notifications
  audit_reminder_enabled: boolean;
  audit_reminder_days_before: number;
  audit_overdue_enabled: boolean;
  audit_overdue_frequency: 'daily' | 'weekly' | 'never';

  // Warranty notifications
  warranty_expiring_enabled: boolean;
  warranty_expiring_days_before: number;

  // Asset notifications
  asset_low_stock_enabled: boolean;
  asset_low_stock_threshold: number;

  // Maintenance notifications
  maintenance_due_enabled: boolean;
  maintenance_due_days_before: number;

  // Recipients
  notification_recipients: NotificationRecipient[];

  // Digest preferences
  daily_digest_enabled: boolean;
  daily_digest_time: string;
  timezone: string;

  // Timestamps
  last_audit_reminder_sent_at: string | null;
  last_warranty_check_sent_at: string | null;
  last_maintenance_check_sent_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface NotificationEmailLog {
  id: string;
  organization_id: string;
  notification_type: string;
  subject: string;
  recipients: string[];
  related_items: Record<string, string[]> | null;
  status: 'sent' | 'failed' | 'skipped';
  error_message: string | null;
  sent_at: string;
}

// Default settings for new organizations
const defaultSettings: Partial<NotificationSettings> = {
  audit_reminder_enabled: true,
  audit_reminder_days_before: 7,
  audit_overdue_enabled: true,
  audit_overdue_frequency: 'daily',
  warranty_expiring_enabled: true,
  warranty_expiring_days_before: 30,
  asset_low_stock_enabled: false,
  asset_low_stock_threshold: 5,
  maintenance_due_enabled: true,
  maintenance_due_days_before: 7,
  notification_recipients: [],
  daily_digest_enabled: false,
  daily_digest_time: '09:00:00',
  timezone: 'America/New_York',
};

async function fetchNotificationSettings(organizationId: string): Promise<NotificationSettings> {
  const { data, error } = await supabase
    .from('notification_settings')
    .select('*')
    .eq('organization_id', organizationId)
    .single();

  if (error) {
    // If no settings exist yet, create default settings
    if (error.code === 'PGRST116') {
      const { data: newSettings, error: createError } = await supabase
        .from('notification_settings')
        .insert({ organization_id: organizationId })
        .select()
        .single();

      if (createError) throw createError;
      return { ...defaultSettings, ...newSettings } as NotificationSettings;
    }
    throw error;
  }

  return data;
}

async function updateNotificationSettings(
  organizationId: string,
  settings: Partial<NotificationSettings>
): Promise<NotificationSettings> {
  // Remove fields that shouldn't be updated directly
  const { id, organization_id, created_at, updated_at, ...updateData } = settings;

  const { data, error } = await supabase
    .from('notification_settings')
    .update(updateData)
    .eq('organization_id', organizationId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

async function fetchNotificationLogs(organizationId: string, limit = 50): Promise<NotificationEmailLog[]> {
  const { data, error } = await supabase
    .from('notification_email_log')
    .select('*')
    .eq('organization_id', organizationId)
    .order('sent_at', { ascending: false })
    .limit(limit);

  if (error) throw error;
  return data || [];
}

async function triggerNotifications(organizationId: string): Promise<{ message: string; results: any }> {
  const { data, error } = await supabase.functions.invoke('send-audit-notifications', {
    body: { organizationId },
  });

  if (error) throw error;
  return data;
}

export function useNotificationSettings(organizationId: string) {
  return useQuery({
    queryKey: ['notification-settings', organizationId],
    queryFn: () => fetchNotificationSettings(organizationId),
    enabled: !!organizationId,
  });
}

export function useUpdateNotificationSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      organizationId,
      settings,
    }: {
      organizationId: string;
      settings: Partial<NotificationSettings>;
    }) => updateNotificationSettings(organizationId, settings),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['notification-settings', variables.organizationId],
      });
      notifications.show({
        title: 'Settings updated',
        message: 'Notification settings have been saved successfully',
        color: 'green',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to update notification settings. Please try again.',
        color: 'red',
      });
    },
  });
}

export function useNotificationLogs(organizationId: string, limit = 50) {
  return useQuery({
    queryKey: ['notification-logs', organizationId, limit],
    queryFn: () => fetchNotificationLogs(organizationId, limit),
    enabled: !!organizationId,
  });
}

export function useTriggerNotifications() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (organizationId: string) => triggerNotifications(organizationId),
    onSuccess: (data, organizationId) => {
      queryClient.invalidateQueries({ queryKey: ['notification-logs', organizationId] });
      notifications.show({
        title: 'Notifications sent',
        message: data.message || 'Notification check completed',
        color: 'green',
      });
    },
    onError: (error) => {
      notifications.show({
        title: 'Error',
        message: 'Failed to send notifications. Please try again.',
        color: 'red',
      });
    },
  });
}
